/**
 * Buyer → supplier/broker invitation copy. Buyer-led and confident: Cannafax is
 * how this buyer now manages purchasing; privacy is framed as a private network,
 * never as a reassurance or a price pitch.
 */
export type SupplierInviteTone = 'default' | 'broker' | 'direct';

export interface SupplierInviteTemplate {
  id: SupplierInviteTone;
  label: string;
  text: string;
}

export const SUPPLIER_INVITE_TEMPLATES: SupplierInviteTemplate[] = [
  {
    id: 'default',
    label: 'Supplier',
    text:
      "I'm now managing purchasing opportunities through Cannafax.\n\n" +
      "Please share your current available inventory with me there so I can review it alongside active requirements and respond quickly when there's a fit.\n\n" +
      'Cannafax is a private network—not an open marketplace—so we can deal directly and keep opportunities between us.',
  },
  {
    id: 'broker',
    label: 'Broker',
    text:
      "I'm now reviewing current opportunities through Cannafax so I can evaluate available inventory without the usual back-and-forth. " +
      'Please share the listings you represent with me there.\n\n' +
      'Cannafax is a private network—not an open marketplace—so we can deal directly and your supplier relationships stay protected.',
  },
  {
    id: 'direct',
    label: 'Direct',
    text:
      "I'm now consolidating my active purchasing through Cannafax. If you'd like me to consider current and future availability, please share it with me there.\n\n" +
      "I'll review what fits and reach out directly. Cannafax is a private network—not an open marketplace.",
  },
];

/** Full message: greeting, body, then the join line with the link. */
export function supplierInviteMessage(body: string, url: string, name?: string): string {
  const greeting = name?.trim() ? `Hi ${name.trim()} — ` : 'Hi — ';
  return `${greeting}${body.trim()}\n\nJoin my network here: ${url}`;
}

/** Journey B: lead with the buyer's actual demand, then the standing invitation. */
export function buyRequestInviteBody(requestText: string, base: string): string {
  const need = requestText.trim().replace(/\s+/g, ' ');
  return `I'm looking for: ${need}\n\nDo you carry this? Let me know what you have.\n\n${base}`;
}

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

/** Split pasted text ("a@x.com, b@y.com; Name <c@z.com>") into unique valid emails + leftovers to fix. */
export function parseRecipients(raw: string): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const token = part.replace(/^<|>$/g, '').trim();
    if (!token) continue;
    if (EMAIL.test(token)) {
      const e = token.toLowerCase();
      if (!emails.includes(e)) emails.push(e);
    } else if (token.includes('@')) invalid.push(token);
  }
  return { emails, invalid };
}

/** One person pulled from a pasted list. */
export interface SupplierContactDraft { name: string | null; email: string | null; phone: string | null }

const EMAIL_IN = /[^\s@,;<>"'()]+@[^\s@,;<>"'()]+\.[A-Za-z]{2,}/g;
const PHONE_IN = /(?:\+?\d[\d\s().-]{8,}\d)/g;

/** Keep digits (and a leading +). Ten-digit US numbers become +1XXXXXXXXXX. */
export function normalizePhone(raw: string): string | null {
  const plus = raw.trim().startsWith('+');
  const d = raw.replace(/\D/g, '');
  if (d.length < 10 || d.length > 15) return null;
  if (!plus && d.length === 10) return `+1${d}`;
  return `+${d}`;
}

/**
 * Read a pasted supplier list — a spreadsheet column, CSV rows, email signatures,
 * "Name <email>" — line by line. Emails and phones are structured, so this is
 * deterministic; any leftover text on the line becomes the name.
 */
export function parseSupplierList(raw: string): { contacts: SupplierContactDraft[]; invalid: string[] } {
  const contacts: SupplierContactDraft[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const line of raw.split(/\r?\n|;/)) {
    if (!line.trim()) continue;
    const emails = (line.match(EMAIL_IN) ?? []).map((e) => e.toLowerCase());
    let rest = line.replace(EMAIL_IN, ' ');
    const phones = (rest.match(PHONE_IN) ?? []).map(normalizePhone).filter((p): p is string => !!p);
    rest = rest.replace(PHONE_IN, ' ');
    for (const tok of rest.split(/[\s,\t]+/)) if (tok.includes('@')) invalid.push(tok);
    const name = rest.replace(/[<>"'()\t,|]+/g, ' ').replace(/\b(email|e-mail|phone|cell|mobile|tel)\b:?/gi, ' ').replace(/[^\p{L}\p{N}&.' -]/gu, ' ').replace(/\s+/g, ' ').trim().slice(0, 120) || null;
    const people = emails.length ? emails.map((email, i) => ({ email, phone: i === 0 ? phones[0] ?? null : null })) : phones.map((phone) => ({ email: null, phone }));
    for (const p of people) {
      const k = p.email ?? p.phone!;
      if (seen.has(k)) continue;
      seen.add(k);
      contacts.push({ name: people.length === 1 ? name : null, ...p });
    }
  }
  return { contacts, invalid };
}

/** Several open requests in one message, each on its own line. */
export function multiRequestInviteBody(requests: string[], base: string): string {
  const needs = requests.map((r) => r.trim().replace(/\s+/g, ' ')).filter(Boolean);
  if (needs.length === 0) return base;
  if (needs.length === 1) return buyRequestInviteBody(needs[0], base);
  return `I'm looking for:\n${needs.map((n) => `• ${n}`).join('\n')}\n\nDo you carry any of these? Let me know what you have.\n\n${base}`;
}

/** Long BCC lists overflow mail apps; split into batches of this size. */
export const EMAIL_BATCH = 40;
export function batches<T>(xs: T[], size = EMAIL_BATCH): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}
